import { Controller } from "@hotwired/stimulus"

// Used in two modes:
// 1. Message mode: data-link-preview-url-value set → fetch & render OG card inside message
// 2. Input mode: connected to chat form → watch textarea for URLs, show preview above form
export default class extends Controller {
  static targets = ["card", "inputPreview"]
  static values = { url: String }

  connect() {
    // Message mode: url value already set from server
    if (this.hasUrlValue && this.urlValue) {
      this.fetchAndRender(this.urlValue)
    }
  }

  // ── Input mode (called from chat_room_controller) ─────────────────────────

  watchInput(textarea, formEl) {
    this._formEl = formEl
    this._debounceTimer = null
    this._lastUrl = null

    this._boundInput = () => {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => this.detectInputUrl(textarea), 800)
    }
    textarea.addEventListener("input", this._boundInput)
  }

  stopWatchingInput(textarea) {
    if (this._boundInput) textarea.removeEventListener("input", this._boundInput)
    clearTimeout(this._debounceTimer)
    this.clearInputPreview()
  }

  detectInputUrl(textarea) {
    const url = this.extractFirstUrl(textarea.value)
    if (url === this._lastUrl) return
    this._lastUrl = url

    if (!url) {
      this.clearInputPreview()
      return
    }
    this.fetchAndShowInputPreview(url)
  }

  async fetchAndShowInputPreview(url) {
    if (!this.hasInputPreviewTarget) return
    const preview = await this.fetchOg(url)
    if (!preview) { this.clearInputPreview(); return }
    this.renderInputPreview(preview)
  }

  renderInputPreview(data) {
    if (!this.hasInputPreviewTarget) return
    const domain = this.extractDomain(data.url || this._lastUrl)
    this.inputPreviewTarget.innerHTML = `
      <div class="flex items-start gap-3 px-3 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        ${data.image ? `<img src="${this.esc(data.image)}" alt="" class="w-14 h-14 object-cover rounded-lg flex-shrink-0" onerror="this.remove()">` : ""}
        <div class="flex-1 min-w-0">
          <p class="text-xs text-gray-400 dark:text-gray-500 truncate">${this.esc(domain)}</p>
          ${data.title ? `<p class="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">${this.esc(data.title)}</p>` : ""}
          ${data.description ? `<p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">${this.esc(data.description)}</p>` : ""}
        </div>
        <button type="button" class="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                data-action="click->link-preview#clearInputPreview">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `
    this.inputPreviewTarget.classList.remove("hidden")
  }

  clearInputPreview() {
    if (!this.hasInputPreviewTarget) return
    this.inputPreviewTarget.innerHTML = ""
    this.inputPreviewTarget.classList.add("hidden")
    this._lastUrl = null
  }

  // ── Message mode ──────────────────────────────────────────────────────────

  async fetchAndRender(url) {
    if (!this.hasCardTarget) return
    const data = await this.fetchOg(url)
    if (!data) return
    this.renderCard(data, url)
  }

  renderCard(data, originalUrl) {
    if (!this.hasCardTarget) return
    const href = data.url || originalUrl
    const domain = this.extractDomain(href)

    this.cardTarget.innerHTML = `
      <a href="${this.esc(href)}" target="_blank" rel="noopener noreferrer"
         class="block mt-2 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden hover:opacity-90 transition-opacity max-w-xs">
        ${data.image ? `
          <div class="h-36 bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <img src="${this.esc(data.image)}" alt="" class="w-full h-full object-cover" loading="lazy" onerror="this.parentElement.remove()">
          </div>` : ""}
        <div class="px-3 py-2 bg-white dark:bg-gray-800">
          <p class="text-xs text-red-500 dark:text-red-400 font-medium truncate">${this.esc(domain)}</p>
          ${data.title ? `<p class="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 mt-0.5">${this.esc(data.title)}</p>` : ""}
          ${data.description ? `<p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">${this.esc(data.description)}</p>` : ""}
        </div>
      </a>
    `
  }

  // ── Shared helpers ────────────────────────────────────────────────────────

  async fetchOg(url) {
    try {
      const res = await fetch(`/api/v1/link_preview?url=${encodeURIComponent(url)}`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return null
      return await res.json()
    } catch {
      return null
    }
  }

  extractFirstUrl(text) {
    const match = text.match(/https?:\/\/[^\s<>"{}|\\^[\]]+/)
    return match?.[0] || null
  }

  extractDomain(url) {
    try { return new URL(url).hostname.replace(/^www\./, "") } catch { return url }
  }

  esc(text) {
    const d = document.createElement("div")
    d.textContent = String(text ?? "")
    return d.innerHTML
  }
}
